import {
  VaultDatabase,
  StaleDraftError,
  pathRevision,
  filesOf,
  type EncryptedMerge,
} from "./database";
import {
  absent,
  present,
  sameFile,
  FOLDER_KEY_NAME,
  isEncryptedAttachment,
  isEncryptedFile,
  type DocumentRecord,
  type FileState,
  type Snapshot,
  type VaultState,
} from "./model";
import { encryptedFolderOf, folderOf, keyFolders } from "./paths";
import { mergeText } from "./merge";
import { FolderKeyring, LockedFolderError } from "./encryption";
import { rebaseAttachmentImages } from "../markdown-image";
export const LOCAL_SAVE_DELAY_MS = 750;
export interface Draft {
  path: string;
  // In an encrypted folder, the decrypted text; `original` stays the stored age file.
  text: string;
  revision: number;
  original: FileState;
  // "new" is a note that does not exist yet and holds nothing worth creating.
  status: "saved" | "new" | "unsaved" | "storage-error" | "conflict";
  editedAt?: number;
  error?: string;
  timer?: ReturnType<typeof setTimeout>;
  retry: number;
}
/** Whether a draft holds text the vault does not have yet. */
export const draftPending = (draft: Draft): boolean =>
  draft.status !== "saved" && draft.status !== "new";
export class DocumentStore extends EventTarget {
  state: VaultState | null = null;
  readonly drafts = new Map<string, Draft>();
  private channel: BroadcastChannel | null = null;
  private saving = new Map<string, Promise<void>>();
  private lastEdit = 0;
  storageError: string | null = null;
  readonly keyring = new FolderKeyring();
  /** Encrypted files an unlocked key could not read. */
  readonly damaged = new Set<string>();
  constructor(readonly database = new VaultDatabase()) {
    super();
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(`turbonotes2:${database.vaultId}`);
      this.channel.onmessage = () => {
        void this.reload().catch((e) => {
          this.storageError = String(e);
          this.emit();
        });
      };
    }
  }
  emit(): void {
    this.dispatchEvent(new Event("change"));
  }
  private reloading: Promise<void> = Promise.resolve();
  /**
   * Reloads run one after another: decrypting takes a moment, and a slow older
   * reload must never land after a newer one.
   */
  reload(): Promise<void> {
    const next = this.reloading.catch(() => {}).then(() => this.load());
    this.reloading = next;
    return next;
  }
  private async load(): Promise<void> {
    const state = await this.database.load();
    // Decrypt before the new state shows, so an unlocked note never flashes empty.
    await this.decryptFolders(state);
    this.state = state;
    this.storageError = null;
    for (const doc of this.state.documents) {
      this.lastEdit = Math.max(this.lastEdit, doc.editedAt ?? 0);
    }
    for (const [path, draft] of this.drafts) {
      const doc = this.state.documents.find((d) => d.path === path);
      if (draft.status === "saved") {
        if (!doc || this.isLocked(path)) {
          this.drafts.delete(path);
          continue;
        }
        draft.text = this.contentsOf(doc);
        draft.revision = doc.revision;
        draft.original = present(doc.text);
        draft.editedAt = doc.editedAt;
      }
      // Unflushed drafts always remain in memory. Save's CAS detects any newer revision.
    }
    this.emit();
  }
  private keyCache: { state: VaultState | null; keys: Set<string> } | null = null;
  private keys(state = this.state): Set<string> {
    if (this.keyCache?.state !== state)
      this.keyCache = { state, keys: keyFolders(state?.documents.map((doc) => doc.path) ?? []) };
    return this.keyCache.keys;
  }
  /** The encrypted folder an encrypted file belongs to, or null for any other file. */
  encryptedFolder(path: string, keys = this.keys()): string | null {
    return isEncryptedFile(path) ? encryptedFolderOf(folderOf(path), keys) : null;
  }
  /** Whether a path is an encrypted file this tab cannot read right now. */
  isLocked(path: string): boolean {
    return isEncryptedFile(path) && !this.keyring.unlocked(this.encryptedFolder(path));
  }
  private contentsOf(doc: DocumentRecord): string {
    if (!isEncryptedFile(doc.path)) return doc.text;
    const folder = this.encryptedFolder(doc.path);
    return (folder && this.keyring.cached(folder, doc.text)) ?? "";
  }
  /** What a note holds, decrypted when its folder is unlocked; empty when it is locked. */
  text(path: string): string {
    const draft = this.drafts.get(path);
    if (draft) return draft.text;
    const doc = this.state?.documents.find((d) => d.path === path);
    return doc ? this.contentsOf(doc) : "";
  }
  private async decryptFolders(state: VaultState): Promise<void> {
    const keys = this.keys(state);
    const stored = new Map<string, Set<string>>();
    const work: Promise<void>[] = [];
    for (const doc of state.documents) {
      const folder = this.encryptedFolder(doc.path, keys);
      if (!this.keyring.unlocked(folder)) continue;
      const kept = stored.get(folder) ?? new Set<string>();
      stored.set(folder, kept);
      for (const text of [doc.text, ...(doc.base.exists ? [doc.base.text] : [])]) {
        kept.add(text);
        if (this.keyring.cached(folder, text) !== undefined) continue;
        work.push(
          this.keyring.decrypt(folder, doc.path, text).then(
            () => void this.damaged.delete(doc.path),
            () => void this.damaged.add(doc.path),
          ),
        );
      }
    }
    await Promise.all(work);
    for (const [folder, kept] of stored) this.keyring.retain(folder, kept);
  }
  /** Opens a folder's key with its passphrase and decrypts everything inside. */
  async unlock(folder: string, passphrase: string): Promise<void> {
    const key = this.state?.documents.find((doc) => doc.path === `${folder}/${FOLDER_KEY_NAME}`);
    if (!key) throw new Error(`“${folder}” is not an encrypted folder.`);
    await this.keyring.unlock(folder, key.text, passphrase);
    await this.reload();
  }
  /**
   * Saves what can be saved, then forgets the folder's key and every decrypted draft.
   * Returns the notes whose last edits could not be saved.
   */
  async lock(folder: string): Promise<string[]> {
    const inside = [...this.drafts.keys()].filter(
      (path) => isEncryptedFile(path) && this.encryptedFolder(path) === folder,
    );
    const unsaved: string[] = [];
    for (const path of inside)
      await this.flush(path).catch(() => unsaved.push(path));
    for (const path of inside) {
      const draft = this.drafts.get(path);
      if (draft?.timer) clearTimeout(draft.timer);
      this.drafts.delete(path);
    }
    this.keyring.lock(folder);
    this.emit();
    return unsaved;
  }
  /**
   * The age file to store for an edit. A text the note already holds keeps its existing
   * encryption, so only a real edit reaches the next checkpoint.
   */
  private async seal(path: string, text: string, original: FileState): Promise<string> {
    const folder = this.encryptedFolder(path);
    if (!this.keyring.unlocked(folder)) throw new LockedFolderError(folder ?? folderOf(path));
    const doc = this.state?.documents.find((d) => d.path === path);
    for (const file of [original, doc ? present(doc.text) : absent, doc?.base ?? absent])
      if (file.exists && this.keyring.cached(folder, file.text) === text) return file.text;
    return this.keyring.encrypt(folder, path, text);
  }
  /**
   * The stored text a note takes to a new path when it is encrypted there, with its
   * image links rebased on its plain text. A plain note moving into an encrypted folder
   * is encrypted from here on. Plain moves are rebased by the database itself.
   */
  async movedText(source: string, destination: string): Promise<string | undefined> {
    if (!isEncryptedFile(destination)) return undefined;
    const doc = this.state?.documents.find((d) => d.path === source);
    if (!doc) return undefined;
    const contents = this.text(source);
    const moved = rebaseAttachmentImages(contents, source, destination);
    // Unchanged text keeps its encryption, so the move alone reaches the checkpoint.
    if (isEncryptedFile(source) && moved === contents) return undefined;
    const folder = this.encryptedFolder(destination);
    if (!this.keyring.unlocked(folder)) throw new LockedFolderError(folder ?? folderOf(destination));
    return this.keyring.encrypt(folder, destination, moved);
  }
  /** Encrypts text the user chose, such as a merged conflict, for its path. */
  async sealed(path: string, file: FileState): Promise<FileState> {
    if (!isEncryptedFile(path) || !file.exists) return file;
    return present(await this.seal(path, file.text, absent));
  }
  /** Decrypts a stored version for reading, such as a conflict side or a revision. */
  async readable(path: string, file: FileState): Promise<FileState> {
    if (!isEncryptedFile(path) || !file.exists) return file;
    const folder = this.encryptedFolder(path);
    if (!this.keyring.unlocked(folder)) throw new LockedFolderError(folder ?? folderOf(path));
    return present(await this.keyring.decrypt(folder, path, file.text));
  }
  /**
   * Three-way merges for encrypted notes changed both here and on GitHub. Ciphertext
   * cannot be merged line by line, so each side is decrypted, merged, and encrypted
   * again. A locked folder holds the refresh back rather than turning edits into
   * conflicts nobody can read.
   */
  async encryptedMerges(state: VaultState, snapshot: Snapshot): Promise<EncryptedMerge[]> {
    const base = filesOf(state, true),
      local = filesOf(state);
    const keys = keyFolders([...state.documents.map((doc) => doc.path), ...Object.keys(snapshot.files)]);
    const merges: EncryptedMerge[] = [];
    for (const [path, remote] of Object.entries(snapshot.files)) {
      const before = base[path] ?? absent,
        mine = local[path] ?? absent;
      if (
        !isEncryptedFile(path) ||
        !remote.exists ||
        !before.exists ||
        !mine.exists ||
        sameFile(mine, remote) ||
        sameFile(before, mine) ||
        sameFile(before, remote)
      )
        continue;
      const folder = encryptedFolderOf(folderOf(path), keys);
      if (!this.keyring.unlocked(folder))
        throw new LockedFolderError(
          folder ?? folderOf(path),
          `Unlock “${folder ?? folderOf(path)}” to bring in its changes from GitHub.`,
        );
      // Two different images have no merge; they become a conflict to choose from.
      if (isEncryptedAttachment(path)) continue;
      const [text, ours, theirs] = await Promise.all(
        [before, mine, remote].map((file) => this.keyring.decrypt(folder, path, file.text)),
      );
      const merged = mergeText(text!, ours!, theirs!);
      if (merged === null) continue;
      merges.push({
        path,
        local: mine.text,
        remote: remote.text,
        merged: await this.keyring.encrypt(folder, path, merged),
      });
    }
    return merges;
  }
  get(path: string): Draft {
    let draft = this.drafts.get(path);
    if (draft) return draft;
    if (this.isLocked(path))
      throw new LockedFolderError(this.encryptedFolder(path) ?? folderOf(path));
    const doc = this.state?.documents.find((d) => d.path === path);
    if (doc && this.damaged.has(path))
      throw new Error(`“${path}” could not be decrypted with its folder's key.`);
    draft = {
      path,
      text: doc ? this.contentsOf(doc) : "",
      revision: this.state ? pathRevision(this.state, path) : 0,
      original: doc ? present(doc.text) : absent,
      status: doc ? "saved" : "new",
      editedAt: doc?.editedAt,
      retry: 0,
    };
    this.drafts.set(path, draft);
    return draft;
  }
  edit(path: string, text: string): void {
    const d = this.get(path);
    d.text = text;
    d.editedAt = this.lastEdit = Math.max(Date.now(), this.lastEdit + 1);
    d.status = "unsaved";
    d.error = undefined;
    if (d.timer) clearTimeout(d.timer);
    d.timer = setTimeout(
      () => void this.flush(path).catch(() => {}),
      LOCAL_SAVE_DELAY_MS,
    );
    this.emit();
  }
  sortByRecentEdit(paths: readonly string[]): string[] {
    const documents = new Map(this.state?.documents.map((doc) => [doc.path, doc]) ?? []);
    const recency = new Map(paths.map((path) => {
      const doc = documents.get(path);
      return [path, {
        editedAt: Math.max(doc?.editedAt ?? 0, this.drafts.get(path)?.editedAt ?? 0),
        revision: doc?.revision ?? 0,
      }];
    }));
    return [...paths].sort((a, b) => {
      const first = recency.get(a)!, second = recency.get(b)!;
      return second.editedAt - first.editedAt || second.revision - first.revision || a.localeCompare(b);
    });
  }
  get unsaved(): boolean {
    return [...this.drafts.values()].some(draftPending);
  }
  async flush(path?: string): Promise<void> {
    if (!path) {
      for (const key of this.drafts.keys()) await this.flush(key);
      return;
    }
    const active = this.saving.get(path);
    if (active) {
      await active;
      if (this.drafts.get(path)?.status === "unsaved") await this.flush(path);
      return;
    }
    const d = this.drafts.get(path);
    if (!d || !draftPending(d)) return;
    if (d.status === "conflict")
      throw new Error(d.error ?? "Resolve the local conflict first.");
    if (d.timer) clearTimeout(d.timer);
    const text = d.text,
      revision = d.revision,
      original = d.original,
      editedAt = d.editedAt;
    const save = (async () => {
      try {
        const stored = isEncryptedFile(path) ? await this.seal(path, text, original) : text;
        const doc = await this.database.save(path, stored, revision, original, editedAt);
        d.revision = doc.revision;
        d.original = present(stored);
        d.retry = 0;
        d.error = undefined;
        d.status = d.text === text ? "saved" : "unsaved";
        this.storageError = null;
        this.channel?.postMessage({ path, revision: doc.revision });
        await this.reload();
      } catch (e) {
        d.status = e instanceof StaleDraftError ? "conflict" : "storage-error";
        d.error = e instanceof Error ? e.message : "Local storage failed.";
        if (e instanceof StaleDraftError) await this.reload().catch(() => {});
        else this.storageError = d.error;
        // Quota and unavailable storage need an explicit retry. Abort/Unknown errors get bounded backoff.
        if (
          e instanceof DOMException &&
          ["AbortError", "UnknownError"].includes(e.name) &&
          d.retry < 3
        ) {
          d.timer = setTimeout(
            () => void this.flush(path).catch(() => {}),
            500 * 2 ** d.retry++,
          );
        }
        this.emit();
        throw e;
      } finally {
        this.saving.delete(path);
        this.emit();
      }
    })();
    this.saving.set(path, save);
    await save;
    if (d.status === "unsaved") await this.flush(path);
  }
  async changed(): Promise<void> {
    this.channel?.postMessage({ reload: true });
    await this.reload();
  }
  /** Forgets a blank note nobody is reading: it was never part of the vault. */
  discardNewDraft(path: string): void {
    if (this.drafts.get(path)?.status !== "new") return;
    this.drafts.delete(path);
  }
  discardResolvedDraft(paths: string[]): void {
    paths.forEach((p) => {
      const d = this.drafts.get(p);
      if (d?.timer) clearTimeout(d.timer);
      this.drafts.delete(p);
    });
  }
  close(): void {
    this.channel?.close();
    for (const d of this.drafts.values()) if (d.timer) clearTimeout(d.timer);
  }
}
